/**
 * Synchronous, dependency-free checks that decide whether this page needs the
 * Supabase auth client at all (ADR-WES1). Anonymous visitors without an auth
 * callback in the URL never download it.
 *
 * The storage key is derived exactly as `createClient` derives its default
 * (`sb-<first label of the project host>-auth-token`), so sessions stored
 * before this change keep working; `supabase-auth-client.test.ts` pins the
 * parity with the library.
 */

/** `sb-<project ref>-auth-token` for a Supabase URL, or null if it is not a URL. */
export function supabaseAuthStorageKey(supabaseUrl: string): string | null {
  try {
    const trimmed = supabaseUrl.trim();
    const url = new URL(trimmed.endsWith("/") ? trimmed : `${trimmed}/`);
    return `sb-${url.hostname.split(".")[0]}-auth-token`;
  } catch {
    return null;
  }
}

/**
 * True when this browser holds a persisted Supabase session (or a pending PKCE
 * verifier) under `storageKey`. It only says "the client is needed"; the client
 * still validates, refreshes or clears what it finds.
 */
export function hasStoredSupabaseSession(storageKey: string | null): boolean {
  if (!storageKey || typeof window === "undefined") return false;
  try {
    const storage = window.localStorage;
    return storage.getItem(storageKey) !== null || storage.getItem(`${storageKey}-code-verifier`) !== null;
  } catch {
    // Storage blocked: supabase-js cannot persist a session here either.
    return false;
  }
}

/** True when the URL carries an auth callback the client must consume. */
export function hasAuthCallbackInUrl(href: string): boolean {
  try {
    const url = new URL(href);
    if (url.searchParams.has("code")) return true;
    const hash = new URLSearchParams(url.hash.replace(/^#/, ""));
    return hash.has("access_token") && hash.has("refresh_token");
  } catch {
    return false;
  }
}
